import React, { useState } from 'react'
import { router, Head } from '@inertiajs/react'
import { Building2, ReceiptText, ShieldCheck, MailCheck } from 'lucide-react'
import ClaimShell from '@/components/claim/ClaimShell'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import StripePaymentElement from '@/components/billing/StripePaymentElement'
import { formatMoney } from '@/lib/format'
import BillingRoleNote from '@/components/billing/BillingRoleNote'

interface QuoteRow {
  id: number
  name: string
  currency: string
  /** Server-resolved by role: null for a combined inventory's following listing (see Billing.tsx). */
  roomCount: number | null
  monthlyAmount: number | null
  /** The listing's combined-inventory role, server-composed; null outside a combined inventory. */
  billingLabel?: string | null
}

interface PaymentCaptureProps {
  currentUser: { id: number; email: string }
  account: { id: number; name: string; billingEmail: string | null } | null
  // SetupIntent client_secret (CreateSetupIntentMutation / RAIL1) — off-session card tokenization.
  clientSecret: string
  // Per-env Stripe publishable key, injected from the backend (never inlined — R-FE-LIB).
  stripePublishableKey: string | null
  // The ONE account-level plan name (the catalog plan the agreement snapshots) — shown
  // once on the quote header, never per property (plan-model refactor §7.4).
  planName: string | null
  properties: QuoteRow[]
}

// PaymentCapture (FE3) — the in-app card-capture step of the claim funnel. Replaces the
// hosted-Checkout redirect: the claimant saves a card via Stripe's PaymentElement (off-session
// tokenization, NOT a charge — the first charge settles later via the scheduler), then we send
// them to the polling PaymentStatus page. SCA / 3D Secure surfaces as a "check your email"
// hand-off (the backend owns the off-session re-auth path).
//
// ZERO money math: amounts are the backend's `monthlyAmount` (PriceStructure#price_for),
// formatted by `formatMoney`, never computed (R-FE-MATH).
export default function PaymentCapture({
  account,
  clientSecret,
  stripePublishableKey,
  planName,
  properties,
}: PaymentCaptureProps) {
  // After confirmSetup resolves requires_action that couldn't complete in-app, the backend
  // owns the off-session re-auth — show the "check your email" hand-off instead of looping.
  const [awaitingReAuth, setAwaitingReAuth] = useState(false)

  function onCardSaved() {
    // Card tokenized. Navigate to the polling status page (GET render) — page navigation, so
    // router.visit, not a <Link> (we trigger it programmatically after the async confirm).
    router.visit('/claim/payment/status')
  }

  return (
    <ClaimShell currentStep={3}>
      <Head title="Add your payment card" />
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Add your payment card</h1>
            <HelpTooltip content="Your card is saved securely for your prepaid subscription. You're charged at the start of each billing period, never before you've confirmed your plan." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            We save your card now and charge it when your access period begins.
          </p>
        </section>

        {/* Account card */}
        {account && (
          <section className="animate-slide-in" style={{ animationDelay: '50ms' }}>
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="flex items-center gap-3 px-5 py-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{account.name}</p>
                  {account.billingEmail && (
                    <p className="truncate text-xs text-muted-foreground">{account.billingEmail}</p>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Quote summary — what they're paying for (line items, no math) */}
        {properties.length > 0 && (
          <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 px-5 py-4">
                <div className="flex items-center gap-2">
                  <ReceiptText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">Your plan</span>
                </div>
                {/* The plan appears ONCE, at account level — it governs every property below. */}
                {planName && (
                  <Badge variant="outline" className="border-primary/30 bg-primary/5 text-[11px] capitalize text-primary">
                    {planName} plan
                  </Badge>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead className="text-right">Rooms</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium text-foreground">
                        {p.name}
                        <BillingRoleNote label={p.billingLabel} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                        {p.roomCount ?? 'Add-on'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-foreground">
                        {p.monthlyAmount === null ? '—' : `${formatMoney(p.monthlyAmount, p.currency)}/mo`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

        {/* Card capture — the in-app PaymentElement (mode 'setup' = tokenize, not charge) */}
        <section className="animate-slide-in pb-8" style={{ animationDelay: '150ms' }}>
          {awaitingReAuth ? (
            <ReAuthHandoff billingEmail={account?.billingEmail} />
          ) : stripePublishableKey ? (
            <StripePaymentElement
              clientSecret={clientSecret}
              publishableKey={stripePublishableKey}
              mode="setup"
              submitLabel="Save card & continue"
              onSuccess={onCardSaved}
              onRequiresAction={() => setAwaitingReAuth(true)}
            />
          ) : (
            <UnconfiguredNotice />
          )}
        </section>
      </div>
    </ClaimShell>
  )
}

// SCA / 3D Secure couldn't complete in-app — the backend emails a re-auth link, so we hand off
// rather than loop. (Mirrors the off-session re-auth path; R-SCA.)
function ReAuthHandoff({ billingEmail }: { billingEmail: string | null | undefined }) {
  return (
    <div className="animate-fade-in-up overflow-hidden rounded-xl border border-amber-200 bg-amber-50/60 p-8 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
        <MailCheck className="h-7 w-7 text-amber-600" />
      </div>
      <h2 className="mt-4 text-base font-semibold text-foreground">One more step: check your email</h2>
      <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
        Your bank needs to verify this card. We&apos;ve sent a secure verification link
        {billingEmail ? (
          <>
            {' '}to <span className="font-medium text-foreground">{billingEmail}</span>
          </>
        ) : (
          ' to your billing email'
        )}
        . Follow it to finish setting up your subscription.
      </p>
    </div>
  )
}

// The publishable key is blank in test/dev (the FakeStripe path never mounts a real form). A
// graceful notice keeps the page from 500-ing / showing an empty card box in those envs.
function UnconfiguredNotice() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
      <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">Secure payment is not configured</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Card capture is unavailable in this environment. Contact support to complete setup.
      </p>
    </div>
  )
}
